import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { api, type DocumentReader } from '../api/client';
import { AvisoError, AvisoExito } from './Aviso';
import { BarraDeFiltros, Buscador, LimpiarFiltros, Segmentado } from './Filtros';
import { Modal } from './Modal';
import { BarraDeAvance } from './BarraDeAvance';
import { AccionIcono } from './AccionIcono';
import { IconoCampana } from './Iconos';

/**
 * Quién tiene que leer este documento y cómo va cada quien.
 *
 * Vive en la ficha del documento porque ahí es donde se hace la pregunta. Antes
 * sólo existía la lista de **pendientes**, en otra pantalla: se podía ver a quién
 * le faltaba, pero no si faltaban dos personas o la mitad de la caja, ni cuándo
 * se le recordó por última vez a nadie — así que la única forma de saber si valía
 * la pena volver a insistir era acordarse.
 *
 * El recordatorio a mano sale **sólo a quien falta**. Mandárselo a quien ya acusó
 * enseña a ignorar los correos del portal, que es exactamente lo que no puede
 * pasar con los que sí son obligatorios.
 */
export function LectoresDelDocumento({ documentId }: { documentId: string }) {
  const { t } = useTranslation();
  const [lectores, setLectores] = useState<DocumentReader[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<'' | 'PENDIENTE' | 'VENCIDO' | 'LEIDO'>('');
  const [busqueda, setBusqueda] = useState('');
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [confirmandoTodos, setConfirmandoTodos] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setLectores(await api.documentReaders(documentId));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [documentId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cuentas = useMemo(
    () => ({
      total: lectores.length,
      leidos: lectores.filter((l) => l.acknowledgedAt).length,
      pendientes: lectores.filter((l) => !l.acknowledgedAt).length,
      vencidos: lectores.filter((l) => !l.acknowledgedAt && l.daysLeft < 0).length,
    }),
    [lectores],
  );

  const visibles = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return lectores.filter((lector) => {
      if (filtro === 'LEIDO' && !lector.acknowledgedAt) return false;
      if (filtro === 'PENDIENTE' && lector.acknowledgedAt) return false;
      if (filtro === 'VENCIDO' && (lector.acknowledgedAt || lector.daysLeft >= 0)) return false;
      if (!texto) return true;
      return (
        lector.fullName.toLowerCase().includes(texto) ||
        lector.username.toLowerCase().includes(texto) ||
        lector.email.toLowerCase().includes(texto)
      );
    });
  }, [lectores, filtro, busqueda]);

  async function recordar(userIds: string[] | undefined, marca: string) {
    setOcupado(marca);
    setError(null);
    setMensaje(null);
    try {
      const resultado = await api.sendReadingReminders(documentId, userIds);
      setConfirmandoTodos(false);
      setMensaje(
        resultado.failed > 0
          ? t('reading.remindersPartly', { sent: resultado.sent, failed: resultado.failed })
          : t('reading.remindersSent', { count: resultado.sent }),
      );
      await cargar();
    } catch (e) {
      setConfirmandoTodos(false);
      setError(e);
    } finally {
      setOcupado(null);
    }
  }

  const fecha = (valor: string) => new Date(valor).toLocaleDateString(i18n.language);

  return (
    <section className="tarjeta">
      <div className="tarjeta__encabezado">
        <h2 className="tarjeta__titulo">{t('reading.readersTitle')}</h2>
        {cuentas.pendientes > 0 && (
          <button
            type="button"
            className="boton boton--discreto"
            disabled={ocupado !== null}
            onClick={() => {
              setMensaje(null);
              setConfirmandoTodos(true);
            }}
          >
            {t('reading.remindPending', { count: cuentas.pendientes })}
          </button>
        )}
      </div>

      <AvisoError error={error} />
      {mensaje && <AvisoExito>{mensaje}</AvisoExito>}

      <BarraDeFiltros>
        <Buscador
          etiqueta={t('reading.searchReader')}
          valor={busqueda}
          onCambio={setBusqueda}
        />
        <Segmentado
          etiqueta={t('reading.readerState')}
          valor={filtro}
          onCambio={(valor) => setFiltro(valor as typeof filtro)}
          opciones={[
            { valor: '', etiqueta: t('reading.allReaders'), conteo: cuentas.total },
            { valor: 'PENDIENTE', etiqueta: t('reading.pending'), conteo: cuentas.pendientes },
            { valor: 'VENCIDO', etiqueta: t('reading.overdue'), conteo: cuentas.vencidos },
            { valor: 'LEIDO', etiqueta: t('reading.read'), conteo: cuentas.leidos },
          ]}
        />
        <LimpiarFiltros
          hay={filtro !== '' || busqueda !== ''}
          onLimpiar={() => {
            setFiltro('');
            setBusqueda('');
          }}
        />
      </BarraDeFiltros>

      {visibles.length === 0 ? (
        <p className="vacio">{t('reading.noReaders')}</p>
      ) : (
        <div className="tabla-envoltura">
          <table>
            <thead>
              <tr>
                <th>{t('users.person')}</th>
                <th>{t('users.email')}</th>
                <th>{t('reading.deadline')}</th>
                <th>{t('reading.readerState')}</th>
                <th title={t('reading.pagesReadHelp')}>{t('team.pagesRead')}</th>
                <th>{t('reading.lastReminder')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibles.map((lector) => (
                <tr key={lector.userId}>
                  <td>{lector.fullName}</td>
                  <td className="mono">{lector.email}</td>
                  <td>{fecha(lector.dueOn)}</td>
                  <td>
                    {lector.acknowledgedAt ? (
                      <span className="pildora pildora--bueno">
                        <span className="pildora__punto" aria-hidden="true" />
                        {t('reading.readOn', { date: fecha(lector.acknowledgedAt) })}
                      </span>
                    ) : (
                      <span
                        className={`pildora pildora--${lector.daysLeft < 0 ? 'critico' : 'atencion'}`}
                      >
                        <span className="pildora__punto" aria-hidden="true" />
                        {lector.daysLeft < 0
                          ? t('reading.overdueBy', { days: Math.abs(lector.daysLeft) })
                          : t('reading.daysLeft', { days: lector.daysLeft })}
                      </span>
                    )}
                  </td>
                  {/* Lectura efectiva: cuántas páginas pasaron de verdad por su
                      pantalla. Distingue «no lo ha abierto» de «le faltan tres». */}
                  <td>
                    {lector.pageCount ? (
                      <BarraDeAvance
                        valor={lector.pagesRead / lector.pageCount}
                        tono={lector.pagesRead >= lector.pageCount ? 'bueno' : 'aviso'}
                        texto={`${lector.pagesRead} / ${lector.pageCount}`}
                        etiqueta={t('team.pagesOf', { read: lector.pagesRead, total: lector.pageCount })}
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                  {/* Sin esta columna, la única forma de saber si ya se le
                      insistió a alguien era acordarse. */}
                  <td>{lector.lastRemindedOn ? fecha(lector.lastRemindedOn) : '—'}</td>
                  <td className="acciones-fila">
                    {!lector.acknowledgedAt && (
                      <AccionIcono
                        etiqueta={t('reading.remind')}
                        icono={<IconoCampana />}
                        disabled={ocupado !== null}
                        onClick={() => void recordar([lector.userId], lector.userId)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="tarjeta__pie">{t('reading.readersNote')}</p>

      {confirmandoTodos && (
        <Modal
          titulo={t('reading.remindConfirm')}
          onCerrar={() => setConfirmandoTodos(false)}
        >
          <p>{t('reading.remindConfirmHint', { count: cuentas.pendientes })}</p>
          <div className="modal__acciones">
            <button
              type="button"
              className="boton boton--fantasma"
              onClick={() => setConfirmandoTodos(false)}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="boton"
              disabled={ocupado !== null}
              onClick={() => void recordar(undefined, 'todos')}
            >
              {t('reading.remindConfirmAction')}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
